import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    MenuItem,
    CircularProgress,
    Backdrop
} from '@mui/material';
import { CreateMatchRequest } from '../models/dtos/FlashBetMatchDto';
import { getFlashBetSports, getFlashBetCategory, getFlashBetCategoriesBySport } from '../Api';
import FlashBetSportDto from '../models/dtos/FlashBetSportDto';
import FlashBetCategoryDto from '../models/dtos/FlashBetCategoryDto';
import { extractErrorMessage, createUserFriendlyErrorMessage } from '../utils/ErrorUtil';

interface AddMatchModalProps {
    open: boolean;
    categoryId: string;
    onSave: (data: CreateMatchRequest) => Promise<void>;
    onClose: () => void;
}

const AddMatchModal: React.FC<AddMatchModalProps> = ({ 
    open, 
    categoryId,
    onSave, 
    onClose
}) => {
    const [formData, setFormData] = useState({
        betsApiId: '',
        sportId: 0,
        categoryId: categoryId
    });
    const [sports, setSports] = useState<FlashBetSportDto[]>([]);
    const [categories, setCategories] = useState<FlashBetCategoryDto[]>([]);
    const [category, setCategory] = useState<FlashBetCategoryDto | null>(null);
    const [errors, setErrors] = useState<{ betsApiId?: string }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            loadData();
            setFormData(prev => ({
                ...prev,
                categoryId: categoryId
            }));
        }
    }, [open, categoryId]);

    const loadData = async () => {
        try {
            const [sportsData, categoryData] = await Promise.all([
                getFlashBetSports(),
                getFlashBetCategory(categoryId)
            ]);
            setSports(sportsData);
            setCategory(categoryData);
            
            if (categoryData) {
                setFormData(prev => ({
                    ...prev,
                    sportId: categoryData.sportId
                }));
                
                // Load all categories for this sport
                const categoriesData = await getFlashBetCategoriesBySport(categoryData.sportId);
                setCategories(categoriesData);
            }
        } catch (error) {
            console.error('Failed to load data:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to load modal data');
            setErrorMessage(errorMsg);
        }
    };

    const handleInputChange = (field: keyof typeof formData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear errors when user starts typing
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
        
        // Clear error message when user starts making changes
        if (errorMessage) {
            setErrorMessage(null);
        }
    };

    const handleSportChange = async (sportId: number) => {
        handleInputChange('sportId', sportId);
        
        try {
            // Load categories for the new sport
            const categoriesData = await getFlashBetCategoriesBySport(sportId);
            setCategories(categoriesData);
            
            // Reset category selection when sport changes
            if (categoriesData.length > 0) {
                // If the current categoryId belongs to this sport, keep it selected
                const currentCategory = categoriesData.find(cat => cat.id === categoryId);
                if (currentCategory) {
                    handleInputChange('categoryId', categoryId);
                } else {
                    // Otherwise, select the first category
                    handleInputChange('categoryId', categoriesData[0].id);
                }
            }
        } catch (error) {
            console.error('Failed to load categories for sport:', error);
            const errorMsg = await extractErrorMessage(error, 'Failed to load categories');
            setErrorMessage(errorMsg);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: { betsApiId?: string } = {};

        if (!formData.betsApiId.trim()) {
            newErrors.betsApiId = 'Bets API Id is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const requestData: CreateMatchRequest = {
                betsApiId: formData.betsApiId,
                sportId: formData.sportId,
                categoryId: formData.categoryId
            };

            await onSave(requestData);
            // Only close modal if save was successful
            handleClose();
        } catch (error) {
            console.error('Failed to create match:', error);
            const errorMsg = await createUserFriendlyErrorMessage('create match', error);
            setErrorMessage(errorMsg);
            // Don't close modal on error - let user retry
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (isLoading) return; // Prevent closing while loading
        
        setFormData({
            betsApiId: '',
            sportId: 0,
            categoryId: categoryId
        });
        setCategories([]);
        setErrors({});
        setErrorMessage(null); // Clear error message on close
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Add New Match
            </DialogTitle>
            <DialogContent>
                <Box component="form" sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Creating match for category: <strong>{categories.find(cat => cat.id === formData.categoryId)?.name || 'Loading...'}</strong>
                    </Typography>
                    
                    {/* Error Message Display */}
                    {errorMessage && (
                        <Typography 
                            variant="subtitle2" 
                            color="error" 
                            sx={{ 
                                mb: 2, 
                                p: 1, 
                                backgroundColor: 'error.light', 
                                color: 'error.contrastText',
                                borderRadius: 1 
                            }}
                        >
                            {errorMessage}
                        </Typography>
                    )}
                    
                    <TextField
                        fullWidth
                        label="Sport"
                        select
                        value={formData.sportId}
                        onChange={(e) => handleSportChange(parseInt(e.target.value))}
                        sx={{ mb: 2 }}
                    >
                        {sports.map((sport) => (
                            <MenuItem key={sport.id} value={sport.id}>
                                {sport.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        label="Category"
                        select
                        value={formData.categoryId}
                        onChange={(e) => handleInputChange('categoryId', e.target.value)}
                        sx={{ mb: 2 }}
                        disabled={categories.length === 0}
                    >
                        {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                                {cat.name} {cat.enabled ? '' : '(Disabled)'}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        label="Bets API Id"
                        value={formData.betsApiId}
                        onChange={(e) => handleInputChange('betsApiId', e.target.value)}
                        error={!!errors.betsApiId}
                        helperText={errors.betsApiId}
                        sx={{ mb: 2 }}
                        placeholder="Enter the API URL for match data"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={isLoading}>
                    Create Match
                </Button>
            </DialogActions>

            {/* Loading Overlay */}
            <Backdrop
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    position: 'absolute',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                }}
                open={isLoading}
            >
                <Box display="flex" flexDirection="column" alignItems="center">
                    <CircularProgress color="inherit" />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Creating match...
                    </Typography>
                </Box>
            </Backdrop>
        </Dialog>
    );
};

export default AddMatchModal;